import { gateCompletionUserIds, isGateCleared } from '../../logic/gateUtils'
import { resolveCheckpointById, type ResolvedCheckpoint } from '../../logic/checkpointUtils'
import ActivityLogic from '../../logic/ActivityLogic'
import type Gate from '../../model/Gate'
import type GateSubmission from '../../model/GateSubmission'
import { useActivityLogs } from '../activity/useActivityLogs'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useCheckpoints } from '../checkpoint/useCheckpoints'
import { useTeam } from '../team/useTeam'
import { useUser } from '../user/useUser'
import { useGates, useTeamGateSubmissions } from './useGates'

export interface ActiveGateContext {
  gate: Gate
  checkpoint: ResolvedCheckpoint
  submissions: GateSubmission[]
  teamMemberCount: number
  submittedCount: number
  userHasSubmitted: boolean
  isCleared: boolean
  isActive: boolean
}

export function useActiveGate(): { data: ActiveGateContext | null; isLoading: boolean } {
  const { data: user } = useUser()
  const { teamID, isLoading: accessLoading } = useChallengeAccess()
  const { data: team, isLoading: teamLoading } = useTeam(teamID)
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs()
  const { data: challengeContext, isLoading: challengeLoading } = useChallengeContext()
  const { data: gates, isLoading: gatesLoading } = useGates()
  const { data: checkpoints, isLoading: checkpointsLoading } = useCheckpoints()
  const { data: submissions, isLoading: submissionsLoading } = useTeamGateSubmissions()

  const isLoading =
    accessLoading ||
    teamLoading ||
    logsLoading ||
    challengeLoading ||
    gatesLoading ||
    checkpointsLoading ||
    submissionsLoading

  if (!user || !team || !activityLogs || !challengeContext || !gates || !checkpoints || !submissions) {
    return { data: null, isLoading }
  }

  const teamProgress = ActivityLogic.getTotalProgress(activityLogs)

  for (const gate of gates) {
    if (teamProgress < gate.checkpoint.threshold) continue

    const checkpoint = resolveCheckpointById(
      checkpoints,
      gate.checkpointID,
      teamProgress,
      challengeContext.goal,
    )
    if (!checkpoint) continue

    const completedUserIds = gateCompletionUserIds(gate, submissions)
    if (isGateCleared(gate, completedUserIds, team.members.length)) continue

    return {
      data: {
        gate,
        checkpoint,
        submissions: submissions.filter((submission) => submission.gateId === gate.id),
        teamMemberCount: team.members.length,
        submittedCount: new Set(completedUserIds).size,
        userHasSubmitted: completedUserIds.includes(user.id),
        isCleared: false,
        isActive: true,
      },
      isLoading: false,
    }
  }

  return { data: null, isLoading: false }
}
